/**
 * Parses a full player name into first and last name
 */
export function parsePlayerName(fullName: string): { firstName: string; lastName: string } {
  const nameParts = fullName.trim().split(' ');
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  } else if (nameParts.length === 2) {
    return { firstName: nameParts[0], lastName: nameParts[1] };
  } else {
    // For names with more than 2 parts, treat the first as first name and the rest as last name
    return { 
      firstName: nameParts[0], 
      lastName: nameParts.slice(1).join(' ') 
    };
  }
}

/**
 * Converts a player name to kebab case for use in external IDs
 */
export function playerNameToKebabCase(playerName: { firstName: string; lastName: string }): string {
  // Handle missing or invalid player name
  if (!playerName || typeof playerName.firstName === 'undefined' || typeof playerName.lastName === 'undefined') {
    return 'unknown-player';
  }
  
  // Handle empty names
  const firstName = playerName.firstName || '';
  const lastName = playerName.lastName || 'unknown';
  
  const fullName = `${firstName} ${lastName}`.trim();
  
  // If we still have an empty string after trimming, return a fallback
  if (!fullName) {
    return 'unknown-player';
  }
  
  return fullName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generates external ID in format: <team-id>-<player-name-kebab-case>
 */
export function generateExternalId(revSportTeamID: string, playerName: { firstName: string; lastName: string }): string {
  const kebabName = playerNameToKebabCase(playerName);
  return `${revSportTeamID}-${kebabName}`;
}
